import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Wifi, WifiOff, Activity, RefreshCw, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Fleet Sync Health card for company admins. Aggregates driver heartbeats
 * into an at-a-glance answer to "how many of my drivers have offline
 * actions still stuck on their phones right now?".
 *
 * Data comes from ``GET /api/admin/sync-health`` which returns:
 *   - total_drivers, online, offline
 *   - drivers_with_backlog, total_pending_ops
 *   - backlog_drivers[]  (top 20)
 * The endpoint is company-scoped; super admins get the whole platform.
 */
const _fmtRelative = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.round(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

const SyncHealthWidget = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/sync-health`, { withCredentials: true });
      setData(res.data);
      setError(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Unable to load sync health';
      setError(typeof msg === 'string' ? msg : 'Unable to load sync health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60_000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  if (loading) {
    return (
      <div
        data-testid="sync-health-widget"
        className="bg-white border border-slate-200 rounded-md p-6"
      >
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading sync health…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        data-testid="sync-health-widget"
        className="bg-white border border-slate-200 rounded-md p-6"
      >
        <div className="text-xs text-slate-500">Sync health unavailable ({error || 'no data'}).</div>
      </div>
    );
  }

  const {
    total_drivers: total = 0,
    online = 0,
    offline = 0,
    drivers_with_backlog: backlogCount = 0,
    total_pending_ops: pendingOps = 0,
    backlog_drivers: backlogDrivers = [],
  } = data;

  const anyBacklog = backlogCount > 0;
  const showEmpty = total === 0;

  return (
    <div
      data-testid="sync-health-widget"
      className="bg-white border border-slate-200 rounded-md p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
            anyBacklog ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
          }`}>
            <Activity className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <h2
              className="text-lg font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              Fleet Sync Health
            </h2>
            <p
              className="text-xs text-slate-500"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              Driver PWA connectivity + offline queue backlog
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={fetchHealth}
          aria-label="Refresh"
          data-testid="sync-health-refresh"
          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {showEmpty ? (
        <p className="text-sm text-slate-500">No active drivers yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                <Wifi className="w-3.5 h-3.5 text-emerald-600" /> Online now
              </div>
              <div data-testid="sync-health-online" className="text-2xl font-bold text-slate-900 mt-1">
                {online}
                <span className="text-xs text-slate-400 font-normal"> / {total}</span>
              </div>
            </div>
            <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                <WifiOff className="w-3.5 h-3.5 text-slate-500" /> Offline
              </div>
              <div data-testid="sync-health-offline" className="text-2xl font-bold text-slate-900 mt-1">
                {offline}
              </div>
            </div>
            <div className={`rounded-md border px-3 py-2.5 ${
              backlogCount > 0
                ? 'border-amber-200 bg-amber-50'
                : 'border-slate-100 bg-slate-50'
            }`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                Backlog drivers
              </div>
              <div
                data-testid="sync-health-backlog-drivers"
                className={`text-2xl font-bold mt-1 ${backlogCount > 0 ? 'text-amber-900' : 'text-slate-900'}`}
              >
                {backlogCount}
              </div>
            </div>
            <div className={`rounded-md border px-3 py-2.5 ${
              pendingOps > 0
                ? 'border-amber-200 bg-amber-50'
                : 'border-slate-100 bg-slate-50'
            }`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                Pending ops
              </div>
              <div
                data-testid="sync-health-total-pending"
                className={`text-2xl font-bold mt-1 ${pendingOps > 0 ? 'text-amber-900' : 'text-slate-900'}`}
              >
                {pendingOps}
              </div>
            </div>
          </div>

          {anyBacklog && (
            <>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                data-testid="sync-health-toggle"
                className="w-full flex items-center justify-between text-xs font-semibold text-[#002FA7] hover:text-[#001f6b] mt-2"
              >
                <span>{expanded ? 'Hide' : 'View'} drivers with backlog ({backlogDrivers.length})</span>
                <ChevronRight
                  className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                />
              </button>
              {expanded && (
                <div
                  data-testid="sync-health-backlog-list"
                  className="mt-3 divide-y divide-slate-100 border border-slate-100 rounded-md overflow-hidden"
                >
                  {backlogDrivers.map((d) => (
                    <div
                      key={d.id}
                      data-testid={`sync-health-driver-${d.id}`}
                      className="flex items-center justify-between px-3 py-2 text-sm"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {d.online ? (
                          <Wifi className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <WifiOff className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="text-slate-900 font-medium truncate">{d.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-400">
                            Last heartbeat {_fmtRelative(d.last_heartbeat_at)}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs font-bold text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
                        {d.pending_ops_count} queued
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {!anyBacklog && (
            <p className="text-xs text-slate-500 mt-2">
              All drivers are caught up — no queued offline actions.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default SyncHealthWidget;
